import { useCallback } from "react";

import { toast } from "sonner";

import {
  RoleplaySessionDocument,
  useInviteUsersToSessionMutation,
  useRevokeAccessFromSessionMutation,
} from "@/graphql";
import {
  FullRoleplaySession,
  ScenarioRoleplaySession,
} from "@/types/roleplays";

import { SessionShareState } from "./useSessionShareState";

interface UseSessionMutationsProps {
  session: FullRoleplaySession | ScenarioRoleplaySession;
  onSuccess: () => void;
}

export function useSessionMutations({
  session,
  onSuccess,
}: UseSessionMutationsProps) {
  const refetchQueries = [
    {
      query: RoleplaySessionDocument,
      variables: { uuid: session?.uuid },
    },
  ];

  const [inviteUsersToSession, { loading: inviteLoading }] =
    useInviteUsersToSessionMutation({
      onCompleted: (response) => {
        if (response?.inviteUsersToSession?.ok) {
          toast.success("Session shared");
          onSuccess();
        }
      },
      onError: () => {
        toast.error("Failed to share session");
      },
      refetchQueries,
    });

  const [revokeAccessFromSession, { loading: revokeLoading }] =
    useRevokeAccessFromSessionMutation({
      onCompleted: (response) => {
        if (response?.revokeAccessFromSession?.ok) {
          toast.success("Access removed successfully");
        }
      },
      onError: () => {
        toast.error("Failed to remove access");
      },
      refetchQueries,
    });

  const inviteUsers = useCallback(
    (state: SessionShareState) => {
      if (!session?.id) return;

      const emails = state.selectedUsers
        .map((user) => user.email)
        .filter(Boolean);

      inviteUsersToSession({
        variables: {
          sessionId: session.id,
          emails,
          isNotified: state.isNotified,
          message: state.message,
        },
      });
    },
    [session?.id, inviteUsersToSession],
  );

  const revokeAccess = useCallback(
    (workspaceMemberId: string) => {
      if (!session?.id) return;

      revokeAccessFromSession({
        variables: {
          sessionId: session.id,
          workspaceMemberId,
        },
      });
    },
    [session?.id, revokeAccessFromSession],
  );

  return {
    inviteLoading,
    revokeLoading,
    inviteUsers,
    revokeAccess,
  };
}
